import { useApolloClient } from '@lessonup/client-integration';
import { FirestoreClientCrudServiceV2, FirestoreDatabaseV2 } from '@lessonup/firebase-database';
import { Timer } from '@lessonup/pin-renderer';
import { once } from 'lodash';
import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createPlayerStore } from '../playerStore';
import { PlayerStore, PlayerStoreState, YjsPlayer } from '../playerStore.types';
import { createPlayerStoreProxy } from '../playerStoreProxy';
import { createFirestoreCrudHandlers, createFirestoreDatabaseHandlers } from '../services/firebase';
import { createRealtimeApolloCrudService } from '../services/realtime/api/apollo/realtimeApiApolloService';
import { RealtimeClientApiService } from '../services/realtime/api/realtimeApolloService.types';
import { createRealtimeTrackingService } from '../services/realtime/tracking/realtimeTrackingService';
import { transformPlayerStoreStateToAssignmentSettingsView } from '../utils/transformers/playerStoreState';
import { TimerControllerProvider } from './TimerContext';

const playerStoreContext = createContext<PlayerStore | null>(null);

export const SimplePlayerStoreContextProvider = playerStoreContext.Provider;

interface InnerStoreProviderProps {
  lessonId: string;
  assignmentId: string;
  apiService: RealtimeClientApiService;
  pinId?: string;
  firestoreDatabase: FirestoreDatabaseV2;
  clientCrudService: FirestoreClientCrudServiceV2;
  onUpdate: (state: PlayerStoreState) => void;
  yjsPlayerData: YjsPlayer;
}

const InnerPlayerStoreContextProvider: React.FC<PropsWithChildren<InnerStoreProviderProps>> = ({
  lessonId,
  assignmentId,
  apiService,
  pinId,
  firestoreDatabase,
  clientCrudService,
  onUpdate,
  yjsPlayerData,
  children,
}) => {
  const trackingService = useMemo(
    () => createRealtimeTrackingService(lessonId, assignmentId),
    [assignmentId, lessonId]
  );
  const firestoreCrudHandlers = useMemo(() => {
    return createFirestoreCrudHandlers(assignmentId, clientCrudService);
  }, [assignmentId, clientCrudService]);

  const onStoreUpdate = useCallback(
    (state: PlayerStoreState) => {
      onUpdate(state);
      const assignmentSettingsView = transformPlayerStoreStateToAssignmentSettingsView(state);
      if (!assignmentSettingsView) return null;

      firestoreCrudHandlers.updateView(assignmentSettingsView);
    },
    [firestoreCrudHandlers, onUpdate]
  );

  const [initialData] = useState(yjsPlayerData);

  const playerStoreInstance = useMemo(() => {
    return createPlayerStore({
      lessonId,
      assignmentId,
      pinId,
      apiService,
      trackingService,
      onUpdate: onStoreUpdate,
      initialData,
    });
  }, [lessonId, assignmentId, pinId, apiService, trackingService, onStoreUpdate, initialData]);

  useEffect(() => {
    const firebaseDatabaseHandlers = createFirestoreDatabaseHandlers(
      assignmentId,
      firestoreDatabase,
      once(onStoreUpdate),
      playerStoreInstance.setters
    );

    return () => {
      firebaseDatabaseHandlers.unsubscribe();
    };
  }, [assignmentId, firestoreDatabase, onStoreUpdate, playerStoreInstance.setters]);

  useEffect(() => {
    playerStoreInstance.setters.syncData(yjsPlayerData);
  }, [playerStoreInstance, yjsPlayerData]);

  const timerController = {
    subscribe: playerStoreInstance.store.subscribeAssignment,
    getState: (pinId: string, timerComponentId: string) =>
      playerStoreInstance.store.timerState(pinId, timerComponentId),
    dispatch: (pinId: string, timerComponentId: string, action: Timer.Action) =>
      playerStoreInstance.store.dispatch({ type: 'timer', pinId, timerComponentId, timerAction: action }),
  };

  return (
    <SimplePlayerStoreContextProvider value={playerStoreInstance.store}>
      <TimerControllerProvider controller={timerController}>{children}</TimerControllerProvider>
    </SimplePlayerStoreContextProvider>
  );
};

interface StoreProviderProps {
  lessonId: string;
  assignmentId: string;
  pinId?: string;
  firestoreDatabase: FirestoreDatabaseV2;
  clientCrudService: FirestoreClientCrudServiceV2;
  onError: () => void;
  onUpdate: (state: PlayerStoreState) => void;
  yjsPlayerData: YjsPlayer;
}

export const PlayerStoreContextProvider: React.FC<PropsWithChildren<StoreProviderProps>> = ({
  lessonId,
  assignmentId,
  pinId,
  firestoreDatabase,
  clientCrudService,
  onUpdate,
  yjsPlayerData,
  children,
}) => {
  const apolloClient = useApolloClient();

  const apiService = useMemo(
    () => createRealtimeApolloCrudService(apolloClient, assignmentId),
    [apolloClient, assignmentId]
  );

  if (!assignmentId) {
    const playerStoreProxy = createPlayerStoreProxy();
    const timerController = {
      subscribe: playerStoreProxy.subscribeAssignment,
      getState: (pinId: string, timerComponentId: string) => playerStoreProxy.timerState(pinId, timerComponentId),
      dispatch: () => {},
    };

    return (
      <SimplePlayerStoreContextProvider value={playerStoreProxy}>
        <TimerControllerProvider controller={timerController}>{children}</TimerControllerProvider>
      </SimplePlayerStoreContextProvider>
    );
  }

  return (
    <InnerPlayerStoreContextProvider
      lessonId={lessonId}
      assignmentId={assignmentId}
      apiService={apiService}
      firestoreDatabase={firestoreDatabase}
      clientCrudService={clientCrudService}
      pinId={pinId}
      onUpdate={onUpdate}
      yjsPlayerData={yjsPlayerData}
    >
      {children}
    </InnerPlayerStoreContextProvider>
  );
};

export const usePlayerStore = () => {
  const playerStore = useContext(playerStoreContext);
  if (!playerStore) {
    throw new Error('usePlayerStore must be used within a PlayerStoreContextProvider');
  }
  return playerStore;
};
